import { UseMutationResult, useMutation, useQuery } from '@tanstack/react-query';
import { ReactNode, useMemo, useState, createContext, useContext, useEffect } from 'react';
import { useSessionStorage } from '../hooks';
import { useAPI, ResponseError } from '@nexelem/api';
import { NAVIGATE_TO_DEFAULT_MACHINE_KEY } from '../hooks/useSessionStorage/sessionStorageKeys';
import { useFaro } from '@nexelem/grafana';
import { PERMS } from '../routes/permissions';
import { AuthChannel } from '../services/AuthBroadcast';

export type User = {
  lastName: string;
  firstName: string;
  passwordChangeNeeded: boolean;
  hidden: boolean;
  blocked: boolean;
  language: string;
  location: string;
  id: number;
  login: string;
  permissions: string[];
  roles: Role[];
  isSuperAdmin: boolean;
  email?: string;
  phone?: string;
};

export type Role = {
  id: number;
  name: string;
  permissions: string[];
};
export type LoginData = {
  userName: string;
  password: string;
  forgotPassword?: string;
  showVirtualKeyboard: boolean;
};

export type LoginChangeData = {
  password: string;
  newUserName: string;
};

export type UserState = {
  isLoading: boolean;
};

export type AuthContextData = {
  user?: User;
  userState: UserState;
  isLoggedIn: boolean;
  error: boolean;
  errorContent: unknown;
  onLogin: UseMutationResult<void, Error, LoginData, unknown>;
  onRfidLogin(rfid: string): void;
  onLogout(): void;
  hasPermission(permission: PERMS | PERMS[]): boolean;
};

export const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const faro = useFaro();
  const api = useAPI();
  const { setItem: setNavigateItem, removeItem: removeNavigateItem } = useSessionStorage<boolean>(
    NAVIGATE_TO_DEFAULT_MACHINE_KEY,
  );

  const [error, setError] = useState(false);
  const [errorContent, setErrorContent] = useState();

  const {
    data: userData,
    refetch,
    isLoading,
    error: _userError,
  } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const user = (await api.getUmLogged()) as unknown as User;

      faro?.api.setUser({
        id: user.login,
        username: `${user.firstName} ${user.lastName}`,
        email: user.email,
      });

      return user;
    },
    retry: false,
  });

  useEffect(() => {
    if (faro?.api && userData) {
      faro.api.setUser({
        id: userData.login,
        username: `${userData.firstName} ${userData.lastName}`,
        email: userData.email,
      });
    }
  }, [faro, userData]);

  const onRfidLogin = useMutation({
    mutationKey: ['user-rfid-login'],
    mutationFn: (rfid: string) => api.postUmRfidLogin({ rfid: { rfid: rfid } }),
    onSuccess: () => {
      setError(false);
      setNavigateItem(true);
      AuthChannel.login();
      return refetch();
    },
    onError: async (error) => {
      if (error instanceof ResponseError) {
        const response = await error.response.json();
        setError(true);
        setErrorContent(response);
      }
    },
  });

  const onLogin = useMutation({
    mutationKey: ['user-login'],
    mutationFn: async ({ userName, password }: LoginData) => {
      try {
        await api
          .postUmLoginNoRedirect({
            login: userName,
            password,
            redirect: '',
          })
          .then(() => {
            setNavigateItem(true);
            AuthChannel.login();
            return refetch();
          });
      } catch (error) {
        if (error instanceof ResponseError) {
          const response = await error.response.json();
          setError(true);
          setErrorContent(response);
        }
      }
    },
  });

  const onLogout = useMutation({
    mutationKey: ['user-logout'],
    mutationFn: () => api.getUmLogout(),
    onSuccess: () => {
      removeNavigateItem();
      // here we don't have access to `useNavigation` hook
      window.location.replace('/');
      faro?.api.resetUser();
      AuthChannel.logout();
    },
  });

  const value: AuthContextData = useMemo(() => {
    return {
      user:
        typeof userData === 'object'
          ? {
              ...userData,
              language: userData.language,
            }
          : undefined,
      userState: {
        isLoading,
      },
      error,
      errorContent,
      isLoggedIn: !!userData,
      onLogin: onLogin,
      onLogout: onLogout.mutate,
      onRfidLogin: onRfidLogin.mutate,
      hasPermission(permission: PERMS | PERMS[]): boolean {
        const permissionsToCheck = Array.isArray(permission) ? permission : [permission];
        if (userData && userData.permissions) {
          return userData.permissions.some((p) => permissionsToCheck.includes(p as PERMS));
        }
        return false;
      },
    };
  }, [userData, isLoading, error, errorContent, onLogin, onLogout.mutate, onRfidLogin.mutate]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): Required<AuthContextData> => useContext(AuthContext) as Required<AuthContextData>;
